/**
 * Custom React Flow edge component that renders ELK's computed bend points
 * as clean SVG paths, avoiding node intersections.
 */

import { memo } from 'react'
import { getBezierPath, getSmoothStepPath } from 'reactflow'

function RoutedEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  markerEnd
}) {
  const bendPoints = data?.bendPoints || []
  const isPrerequisite = data?.isPrerequisite !== false

  let path

  if (bendPoints.length >= 2) {
    // Check if node has been dragged — compare live positions with original bend points
    const first = bendPoints[0]
    const last = bendPoints[bendPoints.length - 1]
    const sourceDiv = Math.abs(sourceX - first.x) + Math.abs(sourceY - first.y)
    const targetDiv = Math.abs(targetX - last.x) + Math.abs(targetY - last.y)

    if (sourceDiv > 1 || targetDiv > 1) {
      // Node was dragged — fall back to smooth-step (orthogonal) routing
      const [smoothPath] = getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: 6
      })
      path = smoothPath
    } else {
      // Positions match layout — use ELK's routed bend points
      path = buildOrthogonalPath(bendPoints)
    }
  } else {
    // Fallback to default bezier
    const [bezierPath] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition
    })
    path = bezierPath
  }

  const strokeColor = style?.stroke || (isPrerequisite ? '#6366f1' : '#9ca3af')

  return (
    <>
      {/* Invisible wider path for easier hover/click targeting */}
      <path
        id={`${id}-hitarea`}
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: 'pointer' }}
      />
      <path
        id={id}
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={style?.strokeWidth || 2}
        strokeDasharray={style?.strokeDasharray || undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd={markerEnd}
        style={{
          opacity: style?.opacity ?? 1,
          transition: 'opacity 0.2s ease'
        }}
      />
    </>
  )
}

/**
 * Build an SVG path from ELK's orthogonal bend points.
 * Uses rounded corners at bend points for a cleaner look.
 */
function buildOrthogonalPath(points) {
  if (points.length < 2) return ''

  const radius = 6 // Corner rounding radius

  let d = `M ${points[0].x} ${points[0].y}`

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const next = points[i + 1]

    // Direction vectors
    const dx1 = curr.x - prev.x
    const dy1 = curr.y - prev.y
    const dx2 = next.x - curr.x
    const dy2 = next.y - curr.y

    // Lengths of segments
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)

    // If segments are too short for rounding, just use the point directly
    const r = Math.min(radius, len1 / 2, len2 / 2)

    if (r < 1 || len1 < 1 || len2 < 1) {
      d += ` L ${curr.x} ${curr.y}`
      continue
    }

    // Point just before the corner
    const beforeX = curr.x - (dx1 / len1) * r
    const beforeY = curr.y - (dy1 / len1) * r

    // Point just after the corner
    const afterX = curr.x + (dx2 / len2) * r
    const afterY = curr.y + (dy2 / len2) * r

    d += ` L ${beforeX} ${beforeY}`
    d += ` Q ${curr.x} ${curr.y} ${afterX} ${afterY}`
  }

  // Final point
  const last = points[points.length - 1]
  d += ` L ${last.x} ${last.y}`

  return d
}

export const RoutedEdge = memo(RoutedEdgeComponent)
